import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  ClipboardList,
  MapPin,
  ListOrdered,
  Package,
  AlertTriangle,
  Truck,
  StickyNote,
  RefreshCw,
} from "lucide-react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import { useModules } from "../../hooks/useModules";
import {
  fetchOrder,
  updateOrder,
  refreshOrder,
  dismissOrderDiscrepancy,
  clearCurrentOrder,
} from "../../store/slices/ordersSlice";
import { withVersion } from "../../utils/concurrency";
import { orderAPI } from "../../services/api";
import Button from "../../components/UI/Button";
import SectionCard from "../../components/UI/SectionCard";
import LoadingSpinner from "../../components/UI/LoadingSpinner";
import ConfirmationDialog from "../../components/modals/ConfirmationDialog";
import { ORDER_STATUS_BADGE, orderTypeBadge } from "./OrdersList";
import type { OrderAddressSnapshot } from "../../types/orders";

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("et-EE") : "—";

const fmtDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString("et-EE") : "—";

const fmtMoney = (v?: number | null, currency?: string | null) =>
  v == null
    ? "—"
    : `${v.toLocaleString("et-EE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? ` ${currency}` : ""}`;

const AddressBlock: React.FC<{
  label: string;
  address?: OrderAddressSnapshot | null;
}> = ({ label, address }) => (
  <div>
    <div className="text-[11px] font-semibold text-[#8A9590] uppercase tracking-wide mb-1">
      {label}
    </div>
    {address ? (
      <div className="text-[13px] text-[#0B0F0E] space-y-0.5">
        {address.name && <div className="font-medium">{address.name}</div>}
        {address.addressLine1 && <div>{address.addressLine1}</div>}
        {address.addressLine2 && <div>{address.addressLine2}</div>}
        <div>
          {[address.postCode, address.city, address.state]
            .filter(Boolean)
            .join(" ")}
        </div>
        {address.country && <div>{address.country}</div>}
        {address.contact && (
          <div className="text-[#8A9590]">{address.contact}</div>
        )}
      </div>
    ) : (
      <div className="text-[13px] text-[#8A9590]">—</div>
    )}
  </div>
);

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <div className="text-[11px] font-semibold text-[#8A9590] uppercase tracking-wide">
      {label}
    </div>
    <div className="text-[13px] text-[#0B0F0E] mt-0.5">{children ?? "—"}</div>
  </div>
);

type SupplementDraft = Record<
  string,
  { weightKgPerUnit: string; volumeM3PerUnit: string }
>;

const OrderDetailPage: React.FC = () => {
  // "Create shipment" leaves this module for another one, so it is shown only
  // when the tenant has that other module too.
  const { has } = useModules();
  const { t } = useTranslation("orders");
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { current: order, loading, saving } = useAppSelector((s) => s.orders);

  const [editing, setEditing] = useState(searchParams.get("edit") === "1");
  const [draft, setDraft] = useState<SupplementDraft>({});
  const [notes, setNotes] = useState("");
  const [refreshPrompt, setRefreshPrompt] = useState<string | null>(null);

  useEffect(() => {
    if (orderId) dispatch(fetchOrder(orderId));
    return () => {
      dispatch(clearCurrentOrder());
    };
  }, [dispatch, orderId]);

  useEffect(() => {
    if (!order) return;
    setNotes(order.internalNotes ?? "");
    const d: SupplementDraft = {};
    for (const item of order.items ?? []) {
      d[item.dynamicsLineId] = {
        weightKgPerUnit: item.weightKgPerUnit?.toString() ?? "",
        volumeM3PerUnit: item.volumeM3PerUnit?.toString() ?? "",
      };
    }
    setDraft(d);
  }, [order]);

  const linkedShipment = useMemo(() => {
    if (!order?.linkedShipmentId) return null;
    return typeof order.linkedShipmentId === "object"
      ? order.linkedShipmentId
      : { _id: order.linkedShipmentId, shipmentNumber: order.linkedShipmentNumber ?? "", status: "" };
  }, [order]);

  if (loading || !order) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const handleCreateShipment = async () => {
    try {
      const res = await orderAPI.getShipmentPrefill(order._id);
      const { shipment: copyData, sourceOrder } = res.data.data;
      navigate("/shipments/new", { state: { copyData, sourceOrder } });
    } catch (e: any) {
      toast.error(e.response?.data?.message || t("toasts.prefillFailed"));
    }
  };

  const handleRefresh = async (confirmed = false) => {
    try {
      const result = await dispatch(
        refreshOrder({ id: order._id, confirmed }),
      ).unwrap();
      toast.success(
        result?.removedFromDynamics
          ? t("toasts.refreshRemoved")
          : t("toasts.refreshed"),
      );
    } catch (e: any) {
      if (e?.code === "SHIPMENT_LINKED") setRefreshPrompt(e.message);
      else toast.error(e?.message || t("toasts.refreshFailed"));
    }
  };

  const handleSave = async () => {
    const items = Object.entries(draft).map(([dynamicsLineId, v]) => ({
      dynamicsLineId,
      weightKgPerUnit:
        v.weightKgPerUnit === "" ? null : Number(v.weightKgPerUnit),
      volumeM3PerUnit:
        v.volumeM3PerUnit === "" ? null : Number(v.volumeM3PerUnit),
    }));
    try {
      await dispatch(
        updateOrder({
          id: order._id,
          data: withVersion({ items, internalNotes: notes }, order),
        }),
      ).unwrap();
      toast.success(t("toasts.saved"));
      setEditing(false);
    } catch (e: any) {
      toast.error(typeof e === "string" ? e : t("toasts.saveFailed"));
    }
  };

  const partnerName =
    (typeof order.partnerId === "object" && order.partnerId?.companyName) ||
    order.partnerNameSnapshot ||
    "—";

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/orders")}
            className="p-1.5 rounded-md text-[#8A9590] hover:text-[#0B0F0E] hover:bg-[#F5F4F0]"
            title={t("detail.back")}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <ClipboardList className="h-6 w-6 text-[#C97A3C]" />
          <div>
            <h1 className="text-xl font-medium text-[#0B0F0E] flex items-center gap-2">
              {order.orderNumber}
              <span
                className={`inline-flex px-2 py-0.5 rounded text-[11px] font-semibold ${orderTypeBadge(order.type)}`}
              >
                {order.type === "sales_order" ? t("list.so") : t("list.po")}
              </span>
              <span
                className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${ORDER_STATUS_BADGE[order.status]}`}
              >
                {t(`status.${order.status}`)}
              </span>
            </h1>
            <p className="text-[13px] text-[#8A9590]">
              {partnerName} · {t("detail.lastSynced")}:{" "}
              {fmtDateTime(order.lastSyncedAt)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleRefresh()}
            isLoading={saving}
            icon={<RefreshCw className="h-4 w-4" />}
          >
            {t("actions.refresh")}
          </Button>
          {has("shipments") &&
            !order.linkedShipmentId &&
            order.status !== "cancelled" && (
            <Button
              variant="primary"
              size="sm"
              onClick={handleCreateShipment}
              icon={<Truck className="h-4 w-4" />}
            >
              {t("detail.createShipment")}
            </Button>
          )}
        </div>
      </div>

      {/* Banners */}
      {order.removedFromDynamics && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 text-[13px] rounded-md px-3 py-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          {t("detail.removedFromDynamics")}
        </div>
      )}
      {!order.autoSyncEnabled && order.status !== "cancelled" && (
        <div className="flex items-start gap-2 bg-gray-50 border border-gray-200 text-gray-700 text-[13px] rounded-md px-3 py-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          {t("detail.autoSyncOff")}
        </div>
      )}
      {order.hasDiscrepancy && (
        <div className="flex items-start justify-between gap-2 bg-amber-50 border border-amber-200 text-amber-900 text-[13px] rounded-md px-3 py-2">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">{t("detail.discrepancyTitle")}</div>
              <div>
                {t("detail.discrepancyBody", {
                  fields: order.discrepancyFields.join(", "),
                })}
              </div>
            </div>
          </div>
          <button
            className="text-amber-800 underline shrink-0"
            onClick={() => dispatch(dismissOrderDiscrepancy(order._id))}
          >
            {t("detail.discrepancyDismiss")}
          </button>
        </div>
      )}

      {/* Order info */}
      <SectionCard icon={ClipboardList} title={t("detail.sectionHeader")}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 py-1">
          <InfoRow label={t("detail.orderNumber")}>{order.orderNumber}</InfoRow>
          <InfoRow label={t("detail.customerReference")}>
            {order.customerReference || "—"}
          </InfoRow>
          <InfoRow label={t("detail.partner")}>{partnerName}</InfoRow>
          <InfoRow label={t("detail.dynamicsStatus")}>
            {order.dynamicsStatus || "—"}
          </InfoRow>
          <InfoRow label={t("detail.orderDate")}>
            {fmtDate(order.orderDate)}
          </InfoRow>
          <InfoRow label={t("detail.requestedDelivery")}>
            {fmtDate(order.requestedDeliveryDate)}
          </InfoRow>
          <InfoRow label={t("detail.currency")}>{order.currency || "—"}</InfoRow>
          <InfoRow label={t("detail.paymentTerms")}>
            {order.paymentTerms || "—"}
          </InfoRow>
          <InfoRow label={t("detail.incoterm")}>{order.incoterm || "—"}</InfoRow>
          <InfoRow label={t("detail.totalValue")}>
            {fmtMoney(order.totalValue, order.currency)}
          </InfoRow>
          <InfoRow label={t("detail.totalValueNet")}>
            {fmtMoney(order.totalValueNet, order.currency)}
          </InfoRow>
          <InfoRow label={t("detail.contactPerson")}>
            {order.purchaser || "—"}
          </InfoRow>
        </div>
      </SectionCard>

      {/* Addresses */}
      <SectionCard
        icon={MapPin}
        title={t("detail.sectionAddresses")}
        note={
          order.shipToFallbackWarning ? t("detail.shipToFallback") : undefined
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-1">
          <AddressBlock label={t("detail.billTo")} address={order.billToAddress} />
          <AddressBlock label={t("detail.shipTo")} address={order.shipToAddress} />
        </div>
      </SectionCard>

      {/* Line items */}
      <SectionCard
        icon={ListOrdered}
        title={t("detail.sectionItems")}
        actions={
          editing ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditing(false)}
              >
                {t("detail.cancelEdit")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                isLoading={saving}
              >
                {t("detail.save")}
              </Button>
            </>
          ) : order.status !== "cancelled" ? (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              {t("detail.editSupplements")}
            </Button>
          ) : undefined
        }
        note={editing ? t("detail.supplementHint") : undefined}
      >
        <div className="overflow-x-auto -mx-3 -my-2">
          <table className="w-full text-[13px]">
            <thead className="bg-[#F5F4F0] text-[#8A9590]">
              <tr>
                <th className="text-left font-medium px-3 py-2">
                  {t("detail.itemCode")}
                </th>
                <th className="text-left font-medium px-3 py-2">
                  {t("detail.itemDescription")}
                </th>
                <th className="text-right font-medium px-3 py-2">
                  {t("detail.itemQty")}
                </th>
                <th className="text-left font-medium px-3 py-2">
                  {t("detail.itemUnit")}
                </th>
                <th className="text-right font-medium px-3 py-2">
                  {t("detail.itemUnitPrice")}
                </th>
                <th className="text-right font-medium px-3 py-2">
                  {t("detail.itemTotal")}
                </th>
                <th className="text-right font-medium px-3 py-2">
                  {t("detail.itemWeight")}
                </th>
                <th className="text-right font-medium px-3 py-2">
                  {t("detail.itemVolume")}
                </th>
              </tr>
            </thead>
            <tbody>
              {(order.items ?? []).map((item) => (
                <tr
                  key={item.dynamicsLineId}
                  className="border-t border-[rgba(11,15,14,0.06)]"
                >
                  <td className="px-3 py-2 whitespace-nowrap">
                    {item.productCode || "—"}
                  </td>
                  <td className="px-3 py-2">
                    {item.productName || item.description}
                  </td>
                  <td className="px-3 py-2 text-right">{item.quantity}</td>
                  <td className="px-3 py-2">{item.unit}</td>
                  <td className="px-3 py-2 text-right">
                    {fmtMoney(item.unitPrice, order.currency)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {fmtMoney(item.lineTotal, order.currency)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {editing ? (
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={draft[item.dynamicsLineId]?.weightKgPerUnit ?? ""}
                        onChange={(e) =>
                          setDraft((prev) => ({
                            ...prev,
                            [item.dynamicsLineId]: {
                              ...prev[item.dynamicsLineId],
                              weightKgPerUnit: e.target.value,
                            },
                          }))
                        }
                        className="w-24 h-8 px-2 text-right text-[13px] border border-[rgba(11,15,14,0.15)] rounded-md focus:ring-2 focus:ring-[#C97A3C] focus:outline-none"
                      />
                    ) : (
                      (item.weightKgPerUnit ?? "—")
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {editing ? (
                      <input
                        type="number"
                        min={0}
                        step="0.001"
                        value={draft[item.dynamicsLineId]?.volumeM3PerUnit ?? ""}
                        onChange={(e) =>
                          setDraft((prev) => ({
                            ...prev,
                            [item.dynamicsLineId]: {
                              ...prev[item.dynamicsLineId],
                              volumeM3PerUnit: e.target.value,
                            },
                          }))
                        }
                        className="w-24 h-8 px-2 text-right text-[13px] border border-[rgba(11,15,14,0.15)] rounded-md focus:ring-2 focus:ring-[#C97A3C] focus:outline-none"
                      />
                    ) : (
                      (item.volumeM3PerUnit ?? "—")
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Linked shipment */}
      <SectionCard icon={Package} title={t("detail.sectionShipment")}>
        {linkedShipment ? (
          <div className="flex items-center justify-between py-1">
            <div className="flex items-center gap-2 text-[13px]">
              <Package className="h-4 w-4 text-sky-600" />
              <span className="font-medium">
                {linkedShipment.shipmentNumber}
              </span>
              {linkedShipment.status && (
                <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-sky-50 text-sky-700">
                  {linkedShipment.status}
                </span>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/shipments/${linkedShipment._id}`)}
            >
              {t("detail.openShipment")}
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between py-1">
            <span className="text-[13px] text-[#8A9590]">
              {t("detail.noShipment")}
            </span>
            {has("shipments") && order.status !== "cancelled" && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleCreateShipment}
                icon={<Truck className="h-4 w-4" />}
              >
                {t("detail.createShipment")}
              </Button>
            )}
          </div>
        )}
      </SectionCard>

      {/* Internal notes */}
      <SectionCard icon={StickyNote} title={t("detail.sectionNotes")}>
        {editing ? (
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder={t("detail.notesPlaceholder")}
            className="w-full px-3 py-2 text-[13px] border border-[rgba(11,15,14,0.15)] rounded-md focus:ring-2 focus:ring-[#C97A3C] focus:outline-none"
          />
        ) : (
          <p className="text-[13px] text-[#0B0F0E] whitespace-pre-wrap py-1">
            {order.internalNotes || "—"}
          </p>
        )}
      </SectionCard>

      <ConfirmationDialog
        isOpen={!!refreshPrompt}
        onClose={() => setRefreshPrompt(null)}
        onConfirm={() => handleRefresh(true)}
        title={t("prompts.refreshLinkedTitle")}
        message={refreshPrompt ?? ""}
        confirmText={t("prompts.confirm")}
        type="warning"
      />
    </div>
  );
};

export default OrderDetailPage;
